/**
 * Service for detecting user's country based on IP address
 * Uses multiple APIs with fallback mechanism
 */

export const DEFAULT_COUNTRY = 'SK';

export interface CountryDetectionResult {
  country: string;
  detected: boolean;
}

/**
 * Primary API: country.is
 */
export const resolveCountryByIp = async (ip?: string): Promise<string> => {
  if (!ip) {
    return DEFAULT_COUNTRY;
  }

  let country = DEFAULT_COUNTRY;

  try {
    const response = await fetch(`https://api.country.is/${ip}`);
    const data = await response.json();

    if (data && data.country && data.country !== 'Undefined') {
      country = data.country;
    } else {
      return await resolveCountryByIpSecondApi(ip);
    }
  } catch (error) {
    console.error(`Error fetching country for IP ${ip}: ${error}`);
    return await resolveCountryByIpSecondApi(ip);
  }

  return country;
};

/**
 * Secondary API: ipapi.co
 */
export const resolveCountryByIpSecondApi = async (
  ip: string,
): Promise<string> => {
  let country = DEFAULT_COUNTRY;

  try {
    const response = await fetch(`https://ipapi.co/${ip}/country/`);
    country = await response.text();

    if (
      country &&
      country !== 'Undefined' &&
      !country.includes('RateLimited')
    ) {
      return country;
    } else {
      return await resolveCountryByIpThirdApi(ip);
    }
  } catch (error) {
    console.error(`Error fetching country for IP ${ip} (second API): ${error}`);
    return await resolveCountryByIpThirdApi(ip);
  }
};

/**
 * Tertiary API: ip-api.com
 */
export const resolveCountryByIpThirdApi = async (
  ip: string,
): Promise<string> => {
  let country = DEFAULT_COUNTRY;

  try {
    const response = await fetch(`http://ip-api.com/json/${ip}`);
    const countryData = await response.json();

    if (
      countryData &&
      countryData.countryCode &&
      countryData.countryCode !== 'Undefined'
    ) {
      country = countryData.countryCode;
    }
  } catch (error) {
    console.error(`Error fetching country for IP ${ip} (third API): ${error}`);
  }

  return country;
};

/**
 * Detect country from browser without IP
 * Uses free API that detects based on the request
 */
export const detectCountryFromBrowser = async (): Promise<CountryDetectionResult> => {
  try {
    const response = await fetch('https://api.country.is/');
    const data = await response.json();

    if (data && data.country) {
      return {
        country: data.country,
        detected: true,
      };
    }
  } catch (error) {
    console.error('Error detecting country from browser:', error);
  }

  return {
    country: DEFAULT_COUNTRY,
    detected: false,
  };
};

/**
 * Resolve locale/language based on country code
 */
export const resolveLocaleByCountry = (
  countryCode: string,
): 'sk' | 'cs' | 'en' => {
  switch (countryCode) {
    case 'SK':
      return 'sk';
    case 'CZ':
      return 'cs';
    default:
      return 'en';
  }
};

/**
 * Resolve popup language based on detected country and current website hostname
 * - If user from SK is on CZ site, show Czech text
 * - If user from CZ is on SK site, show Slovak text
 * - If user from SK/CZ is on international site, show their native language
 * - Otherwise show the website's language
 */
export const resolvePopupLanguage = (
  detectedCountry: string,
  currentHostname: string,
): 'sk' | 'cs' | 'en' => {
  const isSlovakSite = currentHostname.includes('golfcentrum.sk');
  const isCzechSite = currentHostname.includes('supergolf.cz');
  const isInternationalSite = currentHostname.includes('golfcentrum.online');

  // User from SK
  if (detectedCountry === 'SK') {
    if (isCzechSite) return 'cs'; // SK user on CZ site → show Czech
    if (isInternationalSite) return 'sk'; // SK user on international → show Slovak
    return 'sk'; // SK user on SK site (or other) → show Slovak
  }

  // User from CZ
  if (detectedCountry === 'CZ') {
    if (isSlovakSite) return 'sk'; // CZ user on SK site → show Slovak
    if (isInternationalSite) return 'cs'; // CZ user on international → show Czech
    return 'cs'; // CZ user on CZ site (or other) → show Czech
  }

  // Other countries → English
  return 'en';
};

/**
 * Resolve website URL based on country
 */
export const resolveWebsiteByCountry = (countryCode: string): string => {
  switch (countryCode) {
    case 'SK':
      return 'https://www.golfcentrum.sk';
    case 'CZ':
      return 'https://www.supergolf.cz';
    default:
      return 'https://www.golfcentrum.online';
  }
};

/**
 * Check if user should see country popup
 * Returns true if detected country doesn't match current website
 */
export const shouldShowCountryPopup = (
  detectedCountry: string,
  currentHostname: string,
  dismissed: boolean = false,
): boolean => {
  if (dismissed) {
    return false;
  }

  const isSlovakSite = currentHostname.includes('golfcentrum.sk');
  const isCzechSite = currentHostname.includes('supergolf.cz');
  const isInternationalSite = currentHostname.includes('golfcentrum.online');

  // Show popup if user is from SK/CZ but on international site
  if (isInternationalSite && ['SK', 'CZ'].includes(detectedCountry)) {
    return true;
  }
  // Show popup if user is from SK but on CZ site
  if (isCzechSite && detectedCountry === 'SK') {
    return true;
  }

  // Show popup if user is from CZ but on SK site
  if (isSlovakSite && detectedCountry === 'CZ') {
    return true;
  }

  // Show popup if user is from other country but on SK/CZ site
  if (
    (isSlovakSite || isCzechSite) &&
    !['SK', 'CZ'].includes(detectedCountry)
  ) {
    return true;
  }

  return false;
};
